import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DateTime as DT } from 'luxon';
import { useRef } from 'react';
import { Button } from 'react-bootstrap';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import { withLocalCurrency, withPrimaryCurrency } from 'utils/helpers/currency/currencyHelpers';
import EmptyResult from 'components/others/EmptyResult';
import { ObjectVat } from 'modules/objects';
import { useSettings } from 'context/SettingsContext';
import type { ReportObjectsBodyProps } from '../types';

const moneyPrimary = (rate: number, value: number) => {
    if (!value) return '—';
    return withPrimaryCurrency(formatPrice(value), rate);
};

const moneyRate = (value: number) => {
    if (!value) return '—';
    return withLocalCurrency(formatPrice(value));
};

const moneyClass = (value: number, extra = 'mb-1 text-break', successOnPositive = false) => {
    if (value < 0) return `${extra} text-danger`;
    if (successOnPositive && value > 0) return `${extra} text-success`;
    return extra;
};

const ReportObjectsBody = ({
    objectsList,
    onShowSpecification,
    showMaterials,
    showServices,
}: ReportObjectsBodyProps) => {
    const { t } = useTranslation(['Objects']);
    const { localSettings } = useSettings();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const refSummary = useRef(0);
    const refSummaryRate = useRef(0);
    const refMaterials = useRef(0);
    const refMaterialsRate = useRef(0);
    const refServices = useRef(0);
    const refServicesRate = useRef(0);
    const colCount = 4 + (showMaterials ? 1 : 0) + (showServices ? 1 : 0);

    refSummary.current = 0;
    refSummaryRate.current = 0;
    refMaterials.current = 0;
    refMaterialsRate.current = 0;
    refServices.current = 0;
    refServicesRate.current = 0;

    return (
        <tbody>
            {objectsList?.map((vobject) => {
                const dt = DT.fromMillis(vobject?.objectCreated || 0).toLocaleString(DT.DATE_MED);
                const dtFinished = DT.fromMillis(vobject?.objectFinished || 0).toLocaleString(DT.DATE_MED);
                const materialsMargin = Number(vobject?.materialsMargin ?? 0);
                const materialsMarginRate = Number(vobject?.materialsMarginRate ?? 0);
                const servicesMargin = Number(vobject?.servicesMargin ?? 0);
                const servicesMarginRate = Number(vobject?.servicesMarginRate ?? 0);
                const incomeAmount = Number(vobject?.objectPriceSummary ?? 0);
                const incomeAmountRate = Number(vobject?.objectPriceSummaryRate ?? 0);

                if (showMaterials) {
                    refMaterials.current += materialsMargin;
                    refMaterialsRate.current += materialsMarginRate;
                }
                if (showServices) {
                    refServices.current += servicesMargin;
                    refServicesRate.current += servicesMarginRate;
                }
                refSummary.current += incomeAmount;
                refSummaryRate.current += incomeAmountRate;

                return (
                    <tr key={vobject.ID}>
                        <td className="name">
                            <Link to={`/objects/${vobject.ID}`} className="text-decoration-none text-body">
                                <h6 className="mb-1 text-break cursor-pointer">
                                    <span className="me-1">{vobject.objectName || vobject.clientName} </span>
                                </h6>
                                <p className="text-muted f-12 mb-0 my-1">{vobject.clientName}</p>
                            </Link>
                        </td>
                        <td className="date">
                            <h6 className="mb-1 text-break">{dtFinished}</h6>
                            <p className="fw-medium fs-12 mb-0 my-1 text-truncate text-muted">{dt}</p>
                        </td>
                        {showMaterials ? (
                            <td className="materials text-end" style={{ verticalAlign: 'top' }}>
                                <h6 className={moneyClass(materialsMargin, 'mb-1 text-break', true)}>
                                    {moneyPrimary(rate, materialsMargin)}
                                </h6>
                                {!isUahMode && !!materialsMargin && (
                                    <p className={moneyClass(materialsMarginRate, 'f-12 mb-0 my-1', true)}>
                                        {moneyRate(materialsMarginRate)}
                                    </p>
                                )}
                            </td>
                        ) : null}
                        {showServices ? (
                            <td className="services text-end" style={{ verticalAlign: 'top' }}>
                                <h6 className={moneyClass(servicesMargin, 'mb-1 text-break', true)}>
                                    {moneyPrimary(rate, servicesMargin)}
                                </h6>
                                {!isUahMode && !!servicesMargin && (
                                    <p className={moneyClass(servicesMarginRate, 'f-12 mb-0 my-1', true)}>
                                        {moneyRate(servicesMarginRate)}
                                    </p>
                                )}
                            </td>
                        ) : null}
                        <td className="income text-end" style={{ verticalAlign: 'top' }}>
                            <h6 className={moneyClass(incomeAmount)}>
                                <ObjectVat isVat={vobject.objectVat} />
                                {moneyPrimary(rate, incomeAmount)}
                            </h6>
                            {!isUahMode && !!incomeAmount && (
                                <p className={moneyClass(incomeAmountRate, 'f-12 mb-0 my-1')}>
                                    {moneyRate(incomeAmountRate)}
                                </p>
                            )}
                        </td>
                        <td className="action">
                            <Button
                                size="sm"
                                variant="light-secondary"
                                className="p-1 py-0"
                                title={t('objectPopup.specificationHint')}
                                onClick={() => onShowSpecification(vobject)}
                            >
                                <i className="bi bi-card-list"></i>
                            </Button>
                        </td>
                    </tr>
                );
            })}

            {objectsList?.length > 0 ? (
                <tr>
                    <td colSpan={2} />
                    {showMaterials ? (
                        <td className="materials text-end">
                            <h6 className={moneyClass(refMaterials.current, 'mb-1 text-break', true)}>
                                {moneyPrimary(rate, refMaterials.current)}
                            </h6>
                            {!isUahMode && !!refMaterials.current && (
                                <p className={moneyClass(refMaterialsRate.current, 'f-12 mb-0 my-1', true)}>
                                    {moneyRate(refMaterialsRate.current)}
                                </p>
                            )}
                        </td>
                    ) : null}
                    {showServices ? (
                        <td className="services text-end">
                            <h6 className={moneyClass(refServices.current, 'mb-1 text-break', true)}>
                                {moneyPrimary(rate, refServices.current)}
                            </h6>
                            {!isUahMode && !!refServices.current && (
                                <p className={moneyClass(refServicesRate.current, 'f-12 mb-0 my-1', true)}>
                                    {moneyRate(refServicesRate.current)}
                                </p>
                            )}
                        </td>
                    ) : null}
                    <td className="income text-end">
                        <h6 className={moneyClass(refSummary.current)}>
                            {moneyPrimary(rate, refSummary.current)}
                        </h6>
                        {!isUahMode && !!refSummary.current && (
                            <p className={moneyClass(refSummaryRate.current, 'f-12 mb-0 my-1')}>
                                {moneyRate(refSummaryRate.current)}
                            </p>
                        )}
                    </td>
                    <td />
                </tr>
            ) : (
                <tr>
                    <td colSpan={colCount}>
                        <EmptyResult />
                    </td>
                </tr>
            )}
        </tbody>
    );
};

export default ReportObjectsBody;
